/**
 * Phase 36 Batch AG — Effective price label helpers.
 *
 * Renders the right caption for the "Key metrics at $X (…)" copy on
 * Summary, Tax Impact, Reports, etc. The basis enum is set by Batch AF
 * (`effective_purchase_price_basis`) and tells us how the current
 * price was chosen:
 *
 *   * `purchase_price`      → "Original purchase price"
 *   * `user_target_offer`   → "your counter-offer"
 *   * `ai_recommended_offer`→ "AI recommended offer"
 *
 * Legacy deals that pre-date Batch AF have no basis field set; in that
 * case we fall back to the neutral "purchase price" label rather than
 * the misleading "asking" copy the screenshot bug surfaced.
 *
 * IMPORTANT: this is a DISPLAY-only helper. The numeric value rendered
 * alongside the caption MUST come from `deal.purchase_price` (which
 * after Batch AF is always the effective price). Never read
 * `deal.asking_price` for analysis-anchored displays.
 */


/**
 * Returns the short basis-suffix copy: "(your counter-offer)" / "(AI
 * recommended)" / "(purchase price)". The caller is responsible for
 * concatenating it after the dollar amount.
 */
export function effectivePriceBasisSuffix(deal) {
  const basis = deal?.effective_purchase_price_basis;
  if (basis === "user_target_offer") return "your counter-offer";
  if (basis === "ai_recommended_offer") return "AI recommended offer";
  // basis === "purchase_price" OR legacy / undefined → neutral copy.
  // We intentionally avoid the word "asking" here because the price
  // shown (deal.purchase_price) is the analysis anchor — not the
  // listing's asking price.
  return "purchase price";
}


/**
 * Higher-level helper that returns the full sentence used by the
 * Summary tab heading: `at $325,000 (your counter-offer)`. Pass in a
 * money-formatter (we don't want to import useFmt here so this stays a
 * pure helper testable without React).
 */
export function effectivePriceCaption(deal, fmtMoney) {
  const value = Number(deal?.purchase_price) || 0;
  const suffix = effectivePriceBasisSuffix(deal);
  return `at ${fmtMoney(value)} (${suffix})`;
}
